import { PartnerService } from '$lib/services/Partner'
import { AuthentificationService } from '$lib/services/Auth'
import { DictionaryService } from '$lib/services/Dictionary'
import { Partner } from '$lib/types/Partner'
import { User } from '$lib/types/User'
import {
    getCurrentPartnerByUrl,
    setPartner as setPartnerStoreEntry,
    partnerStore
} from '$lib/stores/partner.store'
import { userStore, backofficeUserStore } from '$lib/stores/user'
import { currentDictionary, getCurrentDictionary } from '$lib/stores/currentDictionary'
import type { BackOfficeUser } from '$lib/types/BackOfficeUser'

let partnerPromise: Promise<Partner> | null = null
let userPromise: Promise<User | null> | null = null
let adminPromise: Promise<BackOfficeUser | null> | null = null

export async function ensurePartner(): Promise<Partner> {
    if (partnerPromise) return partnerPromise
    partnerPromise = (async () => {
        try {
            const partnerService = new PartnerService()
            const dictionaryService = new DictionaryService()

            const currentUrl = `https://${window.location.hostname}`
            let partner: Partner | undefined

            try {
                partner = getCurrentPartnerByUrl(currentUrl)
                if (!partner) {
                    partner = await partnerService.getPartnerByUrl(currentUrl)
                    setPartnerStoreEntry(currentUrl, partner)
                }
                if (import.meta.env.VITE_FORCE_MAINTENANCE === 'true') {
                    partner.maintenance = true
                }
            } catch {
                const fallbackUrl =
                    window.location.hostname.includes('backoffice')
                        ? import.meta.env.VITE_BACKOFFICE_URL
                        : import.meta.env.VITE_PARTNER_URL
                if (fallbackUrl) {
                    partner = await partnerService.getPartnerByUrl(fallbackUrl)
                    if (import.meta.env.VITE_FORCE_MAINTENANCE === 'true') partner.maintenance = true
                } else {
                    partner = new Partner()
                }
            }

            if (!partner!.footer) {
                try {
                    const response: any = await partnerService.getDefaultFooter()
                    partner!.footer = response.data.response.text
                } catch {
                    /* keep empty footer */
                }
            }

            if (!getCurrentDictionary()) {
                try {
                    const dictionary = await dictionaryService.getByPartnerId(partner!.id)
                    currentDictionary.set(dictionary)
                } catch {
                    /* dictionary load failure non-fatal */
                }
            }

            partnerStore.set(partner!)
            return partner!
        } catch (e) {
            console.error('ensurePartner: all fallbacks failed, using empty Partner', e)
            const fallback = new Partner()
            partnerStore.set(fallback)
            return fallback
        }
    })()
    return partnerPromise
}

export async function ensureUser(opts: { silent?: boolean } = {}): Promise<User | null> {
    if (userPromise) return userPromise
    userPromise = (async () => {
        const authService = new AuthentificationService()
        try {
            const raw = await authService.getUser()
            const user = new User(raw)
            userStore.set(user)
            return user
        } catch (e) {
            if (!opts.silent) console.warn('ensureUser failed', e)
            userStore.set(null)
            return null
        }
    })()
    return userPromise
}

export async function ensureAdmin(opts: { silent?: boolean } = {}): Promise<BackOfficeUser | null> {
    if (adminPromise) return adminPromise
    adminPromise = (async () => {
        const authService = new AuthentificationService()
        try {
            const admin = (await authService.getAdminUser()) as BackOfficeUser | null
            if (admin) backofficeUserStore.set(admin)
            return admin || null
        } catch (e) {
            if (!opts.silent) console.warn('ensureAdmin failed', e)
            return null
        }
    })()
    return adminPromise
}

export function clearUserCache() {
    userPromise = null
    userStore.set(null)
}

export function clearAdminCache() {
    adminPromise = null
    backofficeUserStore.set(null)
}
